import {
	useDeleteOrganizationAddressMutation,
	useGetOrganizationAddressesQuery,
	useUpdateOrganizationAddressMutation,
} from '@codezo/graphql';
import { MapPin, MoreHorizontal, Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { SettingsCard } from '../../components/ui/settings-card';
import { useAuth } from '../../hooks/useAuth';
import {
	ADDRESS_TYPE_LABEL,
	formatAddressLines,
	type OrganizationAddress,
} from '../../lib/address-utils';
import { AddressModal } from './AddressModal';

/**
 * The organization's own addresses — the client-side twin of the admin client
 * detail section. Same rules: exactly one primary, and it can only move by
 * promoting another.
 */
export function AddressesCard() {
	const { user } = useAuth();
	const organizationId = user?.organizationId || '';

	const { data, loading, refetch } = useGetOrganizationAddressesQuery({
		variables: { organizationId },
		skip: !organizationId,
		fetchPolicy: 'no-cache',
	});

	const addresses = data?.getOrganizationAddresses ?? [];

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<OrganizationAddress | null>(null);
	const [deleteTarget, setDeleteTarget] = useState<OrganizationAddress | null>(null);

	const [updateAddress] = useUpdateOrganizationAddressMutation();
	const [deleteAddress, { loading: deleting }] = useDeleteOrganizationAddressMutation();

	// Primary first — the one orders default to should be the one read first.
	const sorted = [...addresses].sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary));

	const openAdd = () => {
		setEditing(null);
		setEditOpen(true);
	};

	const openEdit = (address: OrganizationAddress) => {
		setEditing(address);
		setEditOpen(true);
	};

	/** Promotion is an ordinary update — `isPrimary` is a field on the address. */
	const handleSetPrimary = async (address: OrganizationAddress) => {
		try {
			await updateAddress({
				variables: {
					id: address.id,
					input: {
						addressLine1: address.addressLine1,
						addressLine2: address.addressLine2 ?? undefined,
						city: address.city,
						stateCode: address.stateCode ?? undefined,
						postalCode: address.postalCode,
						countryCode: address.countryCode,
						googlePlaceId: address.googlePlaceId ?? undefined,
						latitude: address.latitude ?? undefined,
						longitude: address.longitude ?? undefined,
						name: address.name ?? undefined,
						addressType: address.addressType,
						isPrimary: true,
					},
				},
			});
			toast.success('Primary address updated');
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to update primary address');
		}
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		try {
			await deleteAddress({ variables: { id: deleteTarget.id } });
			toast.success('Address removed');
			setDeleteTarget(null);
			refetch();
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to remove address');
		}
	};

	return (
		<>
			<SettingsCard
				title="Addresses"
				description="Where your orders ship and bill to. The primary address is used by default on new quotes."
				headerAction={
					<Button variant="outline" size="sm" onClick={openAdd}>
						<Plus data-icon="inline-start" />
						Add address
					</Button>
				}
			>
				{loading ? (
					<div className="flex flex-col gap-2">
						{Array.from({ length: 2 }).map((_, i) => (
							<div
								key={i}
								className="h-[80px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface-fill"
							/>
						))}
					</div>
				) : addresses.length === 0 ? (
					<div className="flex flex-col items-center gap-2 rounded-[var(--r-3)] border border-dashed border-line-2 py-10 text-center">
						<div className="flex h-9 w-9 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
							<MapPin size={16} />
						</div>
						<div className="text-sm text-text-2">No addresses yet</div>
						<div className="text-xs text-text-4">
							Add one so your quotes and orders have somewhere to ship to.
						</div>
					</div>
				) : (
					<div className="flex flex-col gap-2">
						{sorted.map((address) => (
							<div
								key={address.id}
								className="flex items-start gap-3 rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-4 py-3"
							>
								<div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-[var(--r-2)] border border-line-1 bg-surface-fill text-text-3">
									<MapPin size={12} />
								</div>
								<div className="min-w-0 flex-1">
									<div className="mb-1 flex flex-wrap items-center gap-1.5">
										<span className="text-sm font-medium text-text-1">
											{address.name ||
												ADDRESS_TYPE_LABEL[address.addressType]}
										</span>
										{/* The label is free text, so the type still has to be
										    readable when a row is named "Warehouse". */}
										{address.name && (
											<Badge variant="outline" className="text-2xs">
												{ADDRESS_TYPE_LABEL[address.addressType]}
											</Badge>
										)}
										{address.isPrimary && (
											<Badge variant="secondary" className="text-2xs">
												Primary
											</Badge>
										)}
									</div>
									<div className="text-xs leading-relaxed text-text-3">
										{formatAddressLines(address).map((line) => (
											<div key={line}>{line}</div>
										))}
									</div>
								</div>
								<DropdownMenu>
									<DropdownMenuTrigger className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-text-3 transition-colors hover:bg-surface-hover hover:text-text-1">
										<MoreHorizontal size={14} />
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end">
										<DropdownMenuItem onClick={() => openEdit(address)}>
											<Pencil size={14} /> Edit
										</DropdownMenuItem>
										{!address.isPrimary && (
											<DropdownMenuItem
												onClick={() => handleSetPrimary(address)}
											>
												<Star size={14} /> Set as primary
											</DropdownMenuItem>
										)}
										<DropdownMenuItem
											variant="destructive"
											onClick={() => setDeleteTarget(address)}
											// Removing the primary would leave new quotes
											// without a default. Promote another first.
											disabled={address.isPrimary && addresses.length > 1}
										>
											<Trash2 size={14} />{' '}
											{address.isPrimary && addresses.length > 1
												? 'Set another as primary first'
												: 'Remove'}
										</DropdownMenuItem>
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						))}
					</div>
				)}
			</SettingsCard>

			<AddressModal
				open={editOpen}
				onOpenChange={setEditOpen}
				organizationId={organizationId}
				address={editing}
				isFirstAddress={!editing && addresses.length === 0}
				onSuccess={refetch}
			/>

			<ConfirmDialog
				open={!!deleteTarget}
				onOpenChange={(v) => !v && setDeleteTarget(null)}
				title="Remove address"
				description={`Are you sure you want to remove ${
					deleteTarget?.name || deleteTarget?.addressLine1 || 'this address'
				}? Quotes and orders already using it keep their own copy of the address.`}
				confirmLabel="Remove"
				loading={deleting}
				onConfirm={handleDelete}
			/>
		</>
	);
}
